import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";
import SectionHeading from "../SectionHeading";

const imageVariants = {
  hidden: { opacity: 0, x: -32, scale: 0.98 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const contentVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Section « Recette du moment » : mise en page éditoriale, sans carte
 * glassmorphism — la photo porte sa propre élévation, le texte vit
 * directement sur le fond avec un simple filet d'accent.
 */
const NewDishSection = ({ onViewRecipes }) => {
  const { t } = useLanguage();
  return (
    <section className="py-12 sm:py-16 md:py-20 w-full min-w-0">
      <SectionHeading index="03" kicker={t("featuredLabel")} title={t("newRecipeSectionTitle")} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
        {/* Colonne image */}
        <motion.div
          className="relative order-2 lg:order-1"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={imageVariants}
        >
          <motion.div
            className="relative rounded-2xl overflow-hidden aspect-[4/3] w-full max-w-lg mx-auto shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
            whileHover={{ scale: 1.015 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src="https://images.unsplash.com/photo-1547592166-23ac45744acd?w=1000&h=750&fit=crop&q=85"
              alt={t("newRecipeSectionTitle")}
              className="w-full h-full object-cover object-center"
              decoding="async"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 flex items-center gap-2">
              <span
                className="flex items-center gap-1 font-semibold text-white text-sm sm:text-base"
                aria-label={t("fiveStarsAria")}
              >
                <span className="text-ember-400" aria-hidden>
                  ★
                </span>
                <span>5.0</span>
              </span>
              <span className="text-white/70 text-xs sm:text-sm">
                {t("recommended")}
              </span>
            </div>
          </motion.div>
        </motion.div>

        {/* Colonne contenu */}
        <motion.div
          className="relative order-1 lg:order-2 pl-5 sm:pl-6 border-l-2 border-ember-500/60"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={contentVariants}
        >
          <motion.p
            className="text-ink-500 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-2"
            variants={itemVariants}
          >
            {t("soupName")}
          </motion.p>
          <motion.h3
            className="font-display text-2xl sm:text-3xl md:text-4xl font-medium text-ink-900 mb-5"
            variants={itemVariants}
          >
            {t("recipeOfMoment")}
          </motion.h3>
          <motion.p
            className="text-ink-500 text-sm sm:text-base mb-8 max-w-xl leading-relaxed"
            variants={itemVariants}
          >
            {t("newDishDescription")}
          </motion.p>
          <motion.button
            onClick={onViewRecipes}
            className="group inline-flex items-center gap-2 text-ink-900 font-semibold text-base"
            variants={itemVariants}
          >
            <span className="relative">
              {t("seeRecipe")}
              <span className="absolute left-0 -bottom-0.5 h-px w-full bg-ember-600 scale-x-100 origin-left" />
            </span>
            <span className="transition-transform duration-200 group-hover:translate-x-1" aria-hidden>
              →
            </span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default NewDishSection;
